/* ────────────────────────────────────────────────────────────────────
 * Homepage "What Prinotes does" section (English).
 *
 * Purpose: give the OAuth verification reviewer (and any visitor) a plain
 * description of what the app does and WHY it needs each Google scope.
 * Every feature line is worded to stay consistent with the Privacy Policy
 * "Google & Microsoft Integrations" section (legal-en.ts) so the homepage
 * and policy agree, which reviewers cross-check. Arabic mirror in
 * home-features-ar.ts. No em/en dashes (locked 2026-07-11).
 * ──────────────────────────────────────────────────────────────────── */

export interface HomeFeature {
  title: string;
  body: string;
}

export interface HomeFeaturesDoc {
  eyebrow: string;
  heading: string;
  lead: string;
  features: HomeFeature[];
  privacyBefore: string;
  privacyLinkLabel: string;
  privacyAfter: string;
  limitedUse: string;
}

export const HOME_FEATURES_EN: HomeFeaturesDoc = {
  eyebrow: 'How it works',
  heading: 'How does PRINOTES help you?',
  lead: 'Prinotes records your meetings, transcribes them, and turns each conversation into organized notes, action items, and follow-ups. When you connect your Google or Microsoft account, it uses only the access each feature needs, and you stay in control of what syncs.',
  features: [
    {
      title: 'Records conversations and meetings, manually or automatically, and transcribes them into organized notes',
      body: 'Turn your conversations and meetings into clear notes, summaries, and action items in seconds. Whether you record your meeting live or upload a recording, let Prinotes handle the rest. Automatic recording arrives with the Prinotes app (launching soon).',
    },
    {
      title: 'Helps you invite participants from your contacts',
      body: 'Find the right people faster as you invite attendees or assign action items. Prinotes reads your Google Contacts to suggest names as you type, without changing, storing, or sharing your contacts.',
    },
    {
      title: 'Shows your meetings calendar inside Prinotes',
      body: 'Manage your meetings from one place. Prinotes brings your Google or Outlook Calendar events into one calendar, turns upcoming events into scheduled meetings, and lets you create, edit, delete, and RSVP to events, all with meeting follow-ups when you need them.',
    },
    {
      title: 'Sends action items to Google Tasks or Microsoft To Do',
      body: 'With a single click, Prinotes adds the action items it creates to your Google Tasks or Microsoft To Do list. Sync is limited to Prinotes-created action items and never accesses your other tasks.',
    },
    {
      title: 'Saves your meeting files to your own Google Drive or OneDrive',
      body: 'Prinotes saves each meeting’s recording and transcript straight to your own Google Drive or OneDrive, so you keep a copy in an account you control. On by default, off whenever you want, and it only ever touches the files it creates for you.',
    },
  ],
  privacyBefore: 'Your data stays under your control. Learn how each integration works, and how we protect it, in our ',
  privacyLinkLabel: 'Privacy Policy',
  privacyAfter: '.',
  limitedUse:
    'Prinotes never sells Google or Microsoft data or uses it for advertising or AI model training. Prinotes uses Google data under the Google API Services User Data Policy, including its Limited Use requirements.',
};
